import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Users, FileText, Building, Loader2, Plus, Clock, CheckCircle2, AlertCircle, RotateCcw, Headphones, UserPlus, ToggleLeft, ToggleRight, UserMinus, UserCheck, UserX } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

import { API_BASE as API } from '../../config';

const ROLE_COLORS = {
  super_admin: 'text-red-400 bg-red-400/10',
  admin: 'text-purple-400 bg-purple-400/10',
  manager: 'text-blue-400 bg-blue-400/10',
  member: 'text-emerald-400 bg-emerald-400/10',
  viewer: 'text-slate-400 bg-slate-700',
};

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-lg font-bold text-white">{value}</p>
      </div>
    </div>
  );
}

export default function DevOperator() {
  const [loading, setLoading] = useState(true);
  const [operator, setOperator] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'member' });
  const [addingUser, setAddingUser] = useState(false);
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [existingSearch, setExistingSearch] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState(null);
  const [existingRole, setExistingRole] = useState('member');
  const [addingExisting, setAddingExisting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [opRes, dashRes, usersRes] = await Promise.all([
        axios.get(`${API}/dev/collections/platform_operators`, { withCredentials: true }),
        axios.get(`${API}/operator/dashboard`, { withCredentials: true }),
        axios.get(`${API}/operator/users`, { withCredentials: true }),
      ]);
      const ops = opRes.data?.documents || [];
      setOperator(ops[0] || null);
      setDashboard(dashRes.data);
      setUsers(usersRes.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load operator data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchAuditLogs = async () => {
    if (showAudit) { setShowAudit(false); return; }
    try {
      const res = await axios.get(`${API}/operator/audit-log`, { withCredentials: true });
      setAuditLogs(res.data || []);
      setShowAudit(true);
    } catch { toast.error('Failed to load audit log'); }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddingUser(true);
    try {
      await axios.post(`${API}/dev/operator/users`, newUser, { withCredentials: true });
      toast.success(`${newUser.name} added to operator team`);
      setNewUser({ name: '', email: '', password: '', role: 'member' });
      setShowAddUser(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add user');
    }
    setAddingUser(false);
  };

  const searchAvailableUsers = async (query) => {
    setExistingSearch(query);
    setLoadingAvailable(true);
    try {
      const res = await axios.get(`${API}/dev/operator/users/available`, { params: { search: query }, withCredentials: true });
      setAvailableUsers(res.data || []);
    } catch { setAvailableUsers([]); }
    setLoadingAvailable(false);
  };

  const handleOpenAddExisting = () => {
    setShowAddExisting(true);
    setShowAddUser(false);
    setSelectedExisting(null);
    setExistingRole('member');
    setExistingSearch('');
    searchAvailableUsers('');
  };

  const handleAddExistingMember = async () => {
    if (!selectedExisting) return;
    setAddingExisting(true);
    try {
      await axios.post(`${API}/dev/operator/users/add-existing`, { user_id: selectedExisting.user_id, role: existingRole }, { withCredentials: true });
      toast.success(`${selectedExisting.name} added to operator team`);
      setShowAddExisting(false);
      setSelectedExisting(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add user');
    }
    setAddingExisting(false);
  };

  // Guard: at least one active super_admin + dev must remain
  const activeSuperDevs = users.filter(u => u.role === 'super_admin' && u.is_developer && u.is_active !== false);
  const isLastSuperDev = (userId) => activeSuperDevs.length === 1 && activeSuperDevs[0].user_id === userId;

  const changeRole = async (userId, newRole) => {
    if (newRole !== 'super_admin' && isLastSuperDev(userId)) {
      return toast.error('At least one active Super Admin + Dev must remain');
    }
    try {
      await axios.put(`${API}/dev/users/${userId}`, { role: newRole }, { withCredentials: true });
      toast.success('Role updated');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update role'); }
  };

  const toggleDeveloper = async (userId, currentVal) => {
    if (currentVal && isLastSuperDev(userId)) {
      return toast.error('At least one active Super Admin + Dev must remain');
    }
    try {
      await axios.put(`${API}/dev/users/${userId}`, { is_developer: !currentVal }, { withCredentials: true });
      toast.success(`Developer access ${!currentVal ? 'granted' : 'revoked'}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const removeFromTeam = async (userId, name) => {
    if (isLastSuperDev(userId)) {
      return toast.error('At least one active Super Admin + Dev must remain');
    }
    if (!window.confirm(`Remove ${name} from the operator team?`)) return;
    try {
      await axios.post(`${API}/dev/operator/users/${userId}/remove`, {}, { withCredentials: true });
      toast.success(`${name} removed from operator team`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
  };

  const toggleActive = async (userId, currentlyActive) => {
    if (currentlyActive && isLastSuperDev(userId)) {
      return toast.error('At least one active Super Admin + Dev must remain');
    }
    try {
      await axios.put(`${API}/dev/users/${userId}`, { is_active: !currentlyActive }, { withCredentials: true });
      toast.success(currentlyActive ? 'User deactivated' : 'User activated');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  const sys = dashboard?.system || {};
  const sq = dashboard?.support_queue || {};

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="dev-operator-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
          <Shield className="w-5 h-5 text-indigo-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">Platform Operator</h1>
          <p className="text-xs text-slate-400">{operator?.name || 'Platform Management'}</p>
        </div>
      </div>

      {/* Operator Info */}
      {operator && (
        <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4 mb-6" data-testid="operator-info">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-2 text-xs">
            <div>
              <span className="text-slate-500">Operator ID</span>
              <p className="text-slate-200 font-mono mt-0.5">{operator.operator_id}</p>
            </div>
            <div>
              <span className="text-slate-500">Name</span>
              <p className="text-slate-200 font-medium mt-0.5">{operator.name}</p>
            </div>
            <div>
              <span className="text-slate-500">Created</span>
              <p className="text-slate-200 mt-0.5">{operator.created_at ? new Date(operator.created_at).toLocaleDateString() : '-'}</p>
            </div>
            <div>
              <span className="text-slate-500">Team Size</span>
              <p className="text-slate-200 mt-0.5">{dashboard?.operator_team_size || users.length}</p>
            </div>
          </div>
        </div>
      )}

      {/* System Stats */}
      <h2 className="text-sm font-semibold text-slate-300 mb-3">System Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="system-stats">
        <StatCard label="Total Tenants" value={sys.total_tenants ?? 0} icon={Building} color="bg-blue-500/20 text-blue-400" />
        <StatCard label="Total Users" value={sys.total_users ?? 0} icon={Users} color="bg-emerald-500/20 text-emerald-400" />
        <StatCard label="Total Claims" value={sys.total_claims ?? 0} icon={FileText} color="bg-amber-500/20 text-amber-400" />
        <StatCard label="Active Subs" value={sys.active_subscriptions ?? 0} icon={CheckCircle2} color="bg-purple-500/20 text-purple-400" />
      </div>

      {/* Support Queue */}
      <h2 className="text-sm font-semibold text-slate-300 mb-3">Support Queue</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="support-stats">
        <StatCard label="Pending" value={sq.pending ?? 0} icon={Clock} color="bg-amber-500/20 text-amber-400" />
        <StatCard label="In Progress" value={sq.in_progress ?? 0} icon={Headphones} color="bg-blue-500/20 text-blue-400" />
        <StatCard label="Returned" value={sq.returned ?? 0} icon={RotateCcw} color="bg-orange-500/20 text-orange-400" />
        <StatCard label="Resolved" value={sq.resolved ?? 0} icon={CheckCircle2} color="bg-emerald-500/20 text-emerald-400" />
      </div>

      {/* Team Members */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden mb-6" data-testid="operator-team">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Operator Team ({users.length})</h2>
          <div className="flex items-center gap-2">
            <button onClick={handleOpenAddExisting} className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1" data-testid="add-existing-op-user-btn">
              <UserPlus className="w-3 h-3" /> Add Existing
            </button>
            <Button size="sm" onClick={() => { setShowAddUser(!showAddUser); setShowAddExisting(false); }} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" data-testid="add-operator-user-btn">
              <Plus className="w-3 h-3 mr-1" /> New
            </Button>
          </div>
        </div>

        {/* Add Existing User Form */}
        {showAddExisting && (
          <div className="px-5 py-3 border-b border-slate-800 bg-slate-800/40" data-testid="add-existing-op-form">
            <div className="space-y-2">
              <Input
                value={existingSearch}
                onChange={(e) => searchAvailableUsers(e.target.value)}
                placeholder="Search by name or email..."
                className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs"
                data-testid="existing-op-search"
              />
              <div className="max-h-36 overflow-y-auto rounded border border-slate-700 bg-slate-900">
                {loadingAvailable ? (
                  <p className="px-3 py-2 text-[10px] text-slate-500">Searching...</p>
                ) : availableUsers.length === 0 ? (
                  <p className="px-3 py-2 text-[10px] text-slate-500">No users found</p>
                ) : (
                  availableUsers.map((u) => (
                    <div
                      key={u.user_id}
                      onClick={() => setSelectedExisting(u)}
                      className={`px-3 py-2 flex items-center justify-between cursor-pointer transition-colors ${selectedExisting?.user_id === u.user_id ? 'bg-blue-600/20 border-l-2 border-blue-400' : 'hover:bg-slate-800'}`}
                      data-testid={`available-op-user-${u.user_id}`}
                    >
                      <div>
                        <p className="text-xs text-slate-200">{u.name}</p>
                        <p className="text-[10px] text-slate-500">{u.email}</p>
                      </div>
                      <span className="text-[10px] text-slate-500">{u.tenant_id || 'No org'}</span>
                    </div>
                  ))
                )}
              </div>
              {selectedExisting && (
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">Assign as:</span>
                  <Select value={existingRole} onValueChange={setExistingRole}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-300 h-7 text-xs flex-1" data-testid="existing-op-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAddExistingMember} disabled={addingExisting} className="bg-blue-600 hover:bg-blue-700 h-7 text-xs px-3" data-testid="add-existing-op-submit">
                    {addingExisting ? 'Adding...' : 'Add'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowAddExisting(false)} className="text-slate-400 h-7 text-xs px-3">Cancel</Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add User Form */}
        {showAddUser && (
          <div className="px-5 py-3 border-b border-slate-800 bg-slate-800/40" data-testid="add-operator-user-form">
            <form onSubmit={handleAddUser} className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} placeholder="Full Name *" className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" required data-testid="op-new-user-name" />
                <Input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="Email *" className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" required data-testid="op-new-user-email" />
                <Input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="Password *" className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" required data-testid="op-new-user-password" />
                <Select value={newUser.role} onValueChange={val => setNewUser({ ...newUser, role: val })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-300 h-8 text-xs" data-testid="op-new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={addingUser} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs px-3" data-testid="op-create-user-submit">
                  {addingUser ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  {addingUser ? 'Adding...' : 'Add Member'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowAddUser(false)} className="text-slate-400 h-7 text-xs px-3">Cancel</Button>
              </div>
            </form>
          </div>
        )}

        {/* User List */}
        <div className="divide-y divide-slate-800">
          {users.map(u => {
            const isActive = u.is_active !== false;
            return (
            <div key={u.user_id} className={`px-5 py-3 flex items-center justify-between ${!isActive ? 'opacity-50' : ''}`} data-testid={`op-user-${u.user_id}`}>
              <div className="flex items-center gap-3">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-[10px] font-medium text-slate-300">
                    {u.name?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-200">
                    {u.name}
                    {!isActive && <span className="ml-2 text-[9px] font-semibold text-red-400 bg-red-400/10 px-1.5 py-0.5 rounded">Deactivated</span>}
                  </p>
                  <p className="text-[10px] text-slate-500 flex items-center gap-1.5">
                    {u.email}
                    <span className={`px-1.5 py-px rounded text-[9px] font-medium ${u.auth_provider === 'google' ? 'bg-blue-500/10 text-blue-400' : 'bg-slate-700 text-slate-400'}`} data-testid={`op-auth-${u.user_id}`}>
                      {u.auth_provider === 'google' ? 'Google' : 'Email'}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={u.role} onValueChange={(val) => changeRole(u.user_id, val)}>
                  <SelectTrigger className="w-28 h-7 text-[10px] bg-slate-800 border-slate-700 text-slate-300" data-testid={`op-role-${u.user_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  onClick={() => toggleDeveloper(u.user_id, u.is_developer)}
                  title={u.is_developer ? 'Revoke dev access' : 'Grant dev access'}
                  className={`p-1 rounded transition-colors ${u.is_developer ? 'text-amber-400 hover:text-amber-300' : 'text-slate-600 hover:text-slate-400'}`}
                  data-testid={`op-dev-toggle-${u.user_id}`}
                >
                  {u.is_developer ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => toggleActive(u.user_id, isActive)}
                  title={isActive ? 'Deactivate user' : 'Activate user'}
                  className={`p-1 rounded transition-colors ${isActive ? 'text-slate-600 hover:text-amber-400' : 'text-emerald-500 hover:text-emerald-400'}`}
                  data-testid={`op-active-toggle-${u.user_id}`}
                >
                  {isActive ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => removeFromTeam(u.user_id, u.name)}
                  title="Remove from operator team"
                  className="p-1 rounded text-slate-600 hover:text-red-400 transition-colors"
                  data-testid={`op-remove-${u.user_id}`}
                >
                  <UserMinus className="w-4 h-4" />
                </button>
              </div>
            </div>
            );
          })}
          {users.length === 0 && <p className="px-5 py-6 text-sm text-slate-500 text-center">No operator users found</p>}
        </div>
      </div>

      {/* Audit Log Toggle */}
      <Button variant="outline" size="sm" onClick={fetchAuditLogs} className="border-slate-700 text-slate-300 hover:bg-slate-800 h-8 text-xs mb-4" data-testid="toggle-audit-log-btn">
        <AlertCircle className="w-3 h-3 mr-1.5" />
        {showAudit ? 'Hide' : 'Show'} Operator Audit Log
      </Button>

      {/* Audit Log */}
      {showAudit && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden" data-testid="operator-audit-log">
          <div className="px-5 py-3 border-b border-slate-800">
            <h2 className="text-sm font-semibold text-white">Audit Log ({auditLogs.length})</h2>
          </div>
          <div className="divide-y divide-slate-800 max-h-96 overflow-y-auto">
            {auditLogs.slice(0, 50).map((log, i) => (
              <div key={i} className="px-5 py-2.5 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-slate-500 font-mono shrink-0">{log.action}</span>
                  <span className="text-slate-300 truncate">{log.description}</span>
                </div>
                <span className="text-slate-600 text-[10px] shrink-0 ml-3">
                  {log.created_at ? new Date(log.created_at).toLocaleString() : ''}
                </span>
              </div>
            ))}
            {auditLogs.length === 0 && <p className="px-5 py-6 text-sm text-slate-500 text-center">No audit logs found</p>}
          </div>
        </div>
      )}
    </div>
  );
}
